import { stripFuturesExpiration } from "../shared/utils";

// ─── Tradovate CSV Parser (Performance.csv export) ───────────────────────────
// Columns: symbol,_priceFormat,_priceFormatType,_tickSize,buyFillId,sellFillId,
//          qty,buyPrice,sellPrice,pnl,boughtTimestamp,soldTimestamp,duration
//
// Tradovate quotes any field that contains a comma — notably the `pnl` column
// for amounts ≥ $1,000 (e.g. "$4,620.00"). A naive line.split(",") would break
// such a row into extra cells, shifting every later column and silently
// dropping the row (its timestamps become unparseable). Splitting must respect
// double-quoted fields.
//
// When the system/browser locale is French, Tradovate exports the whole file
// with ';' as the field separator (so a thousands-comma in `pnl` like
// "$4,620.00" no longer needs quoting). The separator is auto-detected from
// the header line. Numbers always keep the English decimal point.

/** A completed trade reconstructed from one CSV row. */
export interface ParsedTradovateTrade {
  symbol: string;
  qty: number;
  entryPrice: number;
  exitPrice: number;
  entryTime: Date;
  exitTime: Date;
  pnl: number;
  type: "long" | "short";
  commission?: number; // TopstepX: auto-calculated from fees (negative value)
}

/**
 * Outcome of parsing a Tradovate CSV.
 * - `empty`     — blank text, or not a CSV at all (e.g. a corrupted download).
 * - `format`    — a header is present but it is not a Tradovate export.
 * - `no-trades` — a valid Tradovate file with zero parseable trade rows.
 */
export type TradovateParseResult =
  | { ok: true; trades: ParsedTradovateTrade[] }
  | { ok: false; reason: "empty" | "format" | "no-trades" };

/**
 * Split one CSV line into cells, treating the separator inside double quotes as
 * literal text. Quote characters are stripped from the returned cells.
 * The Tradovate export never escapes quotes inside a field, so a simple
 * in-quotes flag is sufficient.
 */
export function splitTradovateCSVLine(line: string, separator = ","): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === separator && !inQuotes) {
      cells.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  cells.push(current.trim());

  return cells;
}

/**
 * Pick the field separator by comparing how many ';' vs ',' the header holds.
 * ';' wins only when strictly more frequent, so the default comma export is
 * never misclassified.
 */
function detectSeparator(headerLine: string): "," | ";" {
  const semicolons = (headerLine.match(/;/g) ?? []).length;
  const commas = (headerLine.match(/,/g) ?? []).length;
  return semicolons > commas ? ";" : ",";
}

/**
 * Parse a Tradovate Performance.csv export into completed trades.
 */
export function parseTradovateCSV(text: string): TradovateParseResult {
  const trimmed = (text ?? "").trim();
  // A failed Tradovate download writes the literal string "undefined".
  if (!trimmed || trimmed === "undefined") return { ok: false, reason: "empty" };

  const lines = trimmed.split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length < 1) return { ok: false, reason: "empty" };

  const header = lines[0].toLowerCase();
  const isTradovateHeader =
    header.includes("symbol") &&
    (header.includes("boughttimestamp") || header.includes("buyfillid"));
  if (!isTradovateHeader) return { ok: false, reason: "format" };

  // A header with no data rows is not a usable file.
  if (lines.length < 2) return { ok: false, reason: "empty" };

  const separator = detectSeparator(lines[0]);
  const cols = splitTradovateCSVLine(lines[0], separator).map((c) =>
    c.toLowerCase(),
  );
  const idx = {
    symbol: cols.indexOf("symbol"),
    qty: cols.indexOf("qty"),
    buyPrice: cols.indexOf("buyprice"),
    sellPrice: cols.indexOf("sellprice"),
    pnl: cols.indexOf("pnl"),
    buyTime: cols.indexOf("boughttimestamp"),
    sellTime: cols.indexOf("soldtimestamp"),
  };

  if (idx.symbol === -1 || idx.buyPrice === -1 || idx.sellPrice === -1 || idx.pnl === -1) {
    return { ok: false, reason: "format" };
  }

  const parseDateTime = (s: string): Date | null => {
    if (!s) return null;
    const clean = s.replace(/"/g, "").trim();
    const [datePart, timePart] = clean.split(" ");
    if (!datePart || !timePart) return null;
    const [month, day, year] = datePart.split("/");
    return new Date(`${year}-${month}-${day}T${timePart}`);
  };

  const parsePnl = (raw: string): number => {
    const r = raw.replace(/"/g, "").trim();
    if (/^\$\([\d.,]+\)$/.test(r)) return -parseFloat(r.replace(/[$(),]/g, ""));
    return parseFloat(r.replace(/[$,()]/g, ""));
  };

  const trades: ParsedTradovateTrade[] = [];

  for (let i = 1; i < lines.length; i++) {
    const cells = splitTradovateCSVLine(lines[i], separator);
    if (cells.length < 7) continue;

    const rawSymbol = cells[idx.symbol];
    if (!rawSymbol) continue;
    // Tradovate exports dated contracts (MNQM6); align with TopstepX (root MNQ).
    const symbol = stripFuturesExpiration(rawSymbol);

    const qty = parseFloat(cells[idx.qty]);
    const buyPrice = parseFloat(cells[idx.buyPrice]);
    const sellPrice = parseFloat(cells[idx.sellPrice]);
    const pnl = parsePnl(cells[idx.pnl]);
    const buyTime = idx.buyTime !== -1 ? parseDateTime(cells[idx.buyTime]) : null;
    const sellTime = idx.sellTime !== -1 ? parseDateTime(cells[idx.sellTime]) : null;

    if (isNaN(buyPrice) || isNaN(sellPrice) || isNaN(pnl) ||
        !buyTime || isNaN(buyTime.getTime()) ||
        !sellTime || isNaN(sellTime.getTime())) continue;

    const isShort = buyTime > sellTime;
    const entryTime = isShort ? sellTime : buyTime;
    const exitTime = isShort ? buyTime : sellTime;
    const entryPrice = isShort ? sellPrice : buyPrice;
    const exitPrice = isShort ? buyPrice : sellPrice;

    trades.push({
      symbol,
      qty: isNaN(qty) ? 1 : qty,
      entryPrice,
      exitPrice,
      entryTime,
      exitTime,
      pnl,
      type: isShort ? "short" : "long",
    });
  }

  if (trades.length === 0) return { ok: false, reason: "no-trades" };
  return { ok: true, trades };
}
